import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { DollarSign, Gift, Receipt, RotateCcw } from "lucide-react";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Separator,
} from "@/components/ui";
import { cn, formatCurrency, formatTime } from "@/lib/utils";
import { notify } from "@/lib/notify";
import { usePermissions } from "@/stores";
import { CreateRefundDialog } from "@/features/refunds";
import { useOrder, usePayOrder } from "../api";
import { deriveRefundState } from "../lib/refund-state";
import { statusConfig } from "../lib/status-config";
import { RefundBadge } from "./RefundBadge";
import { PaymentRequiredDialog } from "./PaymentRequiredDialog";
import type { Order, PaymentMethod } from "@/types";

interface OrderDetailsDialogProps {
  order: Order;
  onClose: () => void;
}

export function OrderDetailsDialog({
  order: summaryOrder,
  onClose,
}: OrderDetailsDialogProps) {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [refundOpen, setRefundOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const payOrder = usePayOrder();

  const needsFullFetch =
    summaryOrder.items.length === 0 && (summaryOrder.itemCount ?? 0) > 0;
  const { data: fullOrder } = useOrder(needsFullFetch ? summaryOrder.id : "");
  const order = fullOrder ?? summaryOrder;

  const config = statusConfig[order.status];
  const isPaid = order.payments.length > 0;
  const canPay =
    hasPermission("orders.pay") &&
    !isPaid &&
    order.status !== "cancelled" &&
    order.total > 0;

  const refundState = deriveRefundState(order);
  const isFullyRefunded = refundState.kind === "full";
  const hasPending = refundState.kind === "pending";
  const canRefund =
    hasPermission("refunds.create") &&
    isPaid &&
    order.status !== "cancelled" &&
    !isFullyRefunded &&
    !hasPending;

  const handlePay = async (
    method: PaymentMethod,
    amountTendered: number,
    referenceNumber?: string,
  ) => {
    try {
      await payOrder.mutateAsync({
        id: order.id,
        data: { method, amountTendered, referenceNumber },
      });
      notify.success("Payment recorded");
      setPayOpen(false);
    } catch (err) {
      notify.fromError(err, "Failed to record payment");
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[480px]">
        <DialogHeader>
          <div className="flex items-center justify-between gap-2">
            <DialogTitle className="text-xl">
              Order #{order.orderNumber}
            </DialogTitle>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Badge className={cn(config.bg, config.color, "border")}>
                <config.icon className="mr-1 h-3 w-3" />
                {config.label}
              </Badge>
              <RefundBadge order={order} />
            </div>
          </div>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 rounded-lg bg-muted/50 p-3">
            <div>
              <p className="text-xs text-muted-foreground">Type</p>
              <p className="font-medium capitalize">
                {order.orderType.replace("_", " ")}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Time</p>
              <p className="font-medium">{formatTime(order.createdAt)}</p>
            </div>
            {order.customer && (
              <div className="col-span-2">
                <p className="text-xs text-muted-foreground">Customer</p>
                <p className="font-medium">{order.customer.name}</p>
              </div>
            )}
            {order.notes && (
              <div className="col-span-2">
                <p className="text-xs text-muted-foreground">Notes</p>
                <p className="font-medium">{order.notes}</p>
              </div>
            )}
          </div>
          <ItemsList order={order} />
          <TotalsCard order={order} />
          <PaymentSummary order={order} />
          <RefundsSummary order={order} />
        </div>
        <DialogFooter>
          {canPay && (
            <Button onClick={() => setPayOpen(true)}>
              <DollarSign className="mr-2 h-4 w-4" />
              Pay
            </Button>
          )}
          {canRefund && (
            <Button
              variant="outline"
              className="border-red-200 text-red-700 hover:bg-red-50"
              onClick={() => setRefundOpen(true)}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Refund
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => navigate(`/orders/${order.id}/invoice`)}
          >
            <Receipt className="mr-2 h-4 w-4" />
            Invoice
          </Button>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
      <CreateRefundDialog
        open={refundOpen}
        onOpenChange={setRefundOpen}
        order={order}
      />
      {payOpen && (
        <PaymentRequiredDialog
          order={order}
          onClose={() => setPayOpen(false)}
          onPay={handlePay}
          completeOnPay={false}
          isPending={payOrder.isPending}
        />
      )}
    </Dialog>
  );
}

function ItemsList({ order }: { order: Order }) {
  return (
    <div>
      <h4 className="mb-2 text-sm font-semibold text-muted-foreground">
        Items
      </h4>
      <div className="space-y-2 rounded-lg border p-3">
        {order.items.map((item) => (
          <div
            key={item.id}
            className={cn(
              "flex justify-between",
              item.isComped && "rounded-md bg-green-50 p-2",
            )}
          >
            <div>
              <div className="flex items-center gap-2">
                <p
                  className={cn(
                    "font-medium",
                    item.isComped && "text-muted-foreground line-through",
                  )}
                >
                  {item.quantity}× {item.productName}
                </p>
                {item.isComped && (
                  <Badge
                    variant="outline"
                    className="border-green-300 bg-green-100 text-xs text-green-700"
                  >
                    <Gift className="mr-1 h-3 w-3" />
                    Comp
                  </Badge>
                )}
              </div>
              {item.variantName && (
                <p className="text-sm text-muted-foreground">
                  {item.variantName}
                </p>
              )}
              {item.options.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  + {item.options.map((o) => o.optionName).join(", ")}
                </p>
              )}
              {item.isComped && item.compReason && (
                <p className="text-xs text-green-700">
                  Reason: {item.compReason}
                </p>
              )}
              {item.notes && (
                <p className="text-xs italic text-amber-600">
                  "{item.notes}"
                </p>
              )}
            </div>
            <span
              className={cn(
                "font-medium",
                item.isComped && "text-green-600",
              )}
            >
              {item.isComped ? "Comped" : formatCurrency(item.subtotal)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TotalsCard({ order }: { order: Order }) {
  const hasAdjustments = order.discountAmount > 0 || order.tipAmount > 0;

  return (
    <div className="space-y-1.5 rounded-lg bg-muted/50 p-3">
      {hasAdjustments && (
        <>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Subtotal</span>
            <span>{formatCurrency(order.subtotal)}</span>
          </div>
          {order.discountAmount > 0 && (
            <div className="flex justify-between text-sm text-green-600">
              <span>Discount</span>
              <span>-{formatCurrency(order.discountAmount)}</span>
            </div>
          )}
          {order.tipAmount > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Tip</span>
              <span>{formatCurrency(order.tipAmount)}</span>
            </div>
          )}
          <Separator className="my-2" />
        </>
      )}
      <div className="flex justify-between text-lg font-bold">
        <span>Total</span>
        <span>{formatCurrency(order.total)}</span>
      </div>
    </div>
  );
}

function PaymentSummary({ order }: { order: Order }) {
  if (order.payments.length === 0) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
        <div className="flex items-center gap-2 text-amber-800">
          <DollarSign className="h-4 w-4" />
          <span className="font-semibold">Payment Pending</span>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-green-200 bg-green-50 p-3">
      <h4 className="mb-1 text-sm font-semibold text-green-800">
        Payment Received
      </h4>
      {order.payments.map((payment) => (
        <div
          key={payment.id}
          className="flex justify-between text-sm text-green-700"
        >
          <span className="capitalize">{payment.method}</span>
          <span>{formatCurrency(payment.amountTendered)}</span>
        </div>
      ))}
    </div>
  );
}

function RefundsSummary({ order }: { order: Order }) {
  if (!order.refunds || order.refunds.length === 0) {
    return null;
  }

  return (
    <div className="rounded-lg border border-red-200 bg-red-50/60 p-3">
      <h4 className="mb-1 flex items-center gap-1 text-sm font-semibold text-red-800">
        <RotateCcw className="h-3.5 w-3.5" />
        Refunds
      </h4>
      {order.refunds.map((r) => (
        <div
          key={r.id}
          className="flex justify-between text-sm text-red-700"
        >
          <span className="capitalize">{r.status}</span>
          <span>{formatCurrency(r.amount)}</span>
        </div>
      ))}
    </div>
  );
}
